import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { confirm } from "devextreme/ui/dialog";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { Button } from "devextreme-react/button";
import { LoadPanel, Popup } from "devextreme-react";

import routes from "../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../common/Messages";
// The shared error-message ladder (Architecture.md rule 16). It already covers everything a local
// helper would: axios errors with a response body, PascalCase or camelCase Message, string bodies, and
// the timeout case where error.response is undefined.
import { getApiErrorMessage } from "../../../../common/concurrency";
import { useCommittedFilterRows } from "../../../../common/reportFilters";
import { DisableEnableControls, loadLookupFromStaticDataSource } from "../../../../common/Utils";
import FormField from "../../../../dynamicFormBuilder/FormField";
import CommonReport from "../../../../reports/components/CommonReport";
import OperationResultGrid from "../common/OperationResultGrid";
import { ParametersLayout } from "./Layout";
import { PREVIEW_COLUMNS, PREVIEW_KEY_EXPR } from "./PreviewGridColumns";
import "./updateFreeDaysGracePeriods.css";
import {
    BUTTON,
    COMMENT_MAX_LENGTH,
    DATE_TO_USE_OPTIONS,
    DEFAULT_LAYOUT_NAME,
    DateToUse,
    FIELD,
    LAYOUT_ID,
    LAYOUT_NAME,
    PAGE_TITLE,
    PREVIEW_GRID_KEY,
    PREVIEW_TITLE,
    REPORT_FULL_NAME,
    REPORT_NAME,
    REQUEST_TIMEOUT_MS,
    RESULT_LABELS,
    RESULT_CAPTION,
    RESULT_OK,
    ZERO_CHARGES_OPTIONS,
    ZeroCharges,
} from "./constants";

const BUSY = Object.freeze({ IDLE: "idle", PREVIEWING: "previewing", STARTING: "starting" });

/**
 * Puts a class on the Preview popup's WRAPPER so ../common/operationScreen.css can reach
 * `.dx-popup-content` — the element DevExtreme sizes, and the one that has to become a flex column for
 * the grid to fill it. There is no other hook: the popup renders into an overlay outside this
 * component's DOM subtree.
 *
 * Module-level so the object identity is stable; an inline literal would be a new object every render
 * and make the Popup re-apply it.
 */
const PREVIEW_POPUP_ATTR = { class: "op-preview-popup" };

/**
 * A function, not a module-level constant: Reset must re-evaluate new Date(), and a shared object
 * would be mutated across resets.
 */
const initialFormFields = () => ({
    DateToUse: DateToUse.Calculated,
    AsOfDate: new Date(),
    CalculatedDays: 0,
    ZeroCharges: ZeroCharges.Save,
    Comment: "",
});

const addDays = (date, days) => {
    const result = new Date(date.getTime());
    result.setDate(result.getDate() + days);
    return result;
};

/**
 * The rows of the result dialog: Batch Number and Records Processed, exactly the two values
 * TfrmABD0013 takes. The response also carries MissingReleaseDatesUpdated, but the legacy dialog has no
 * slot for it and it is not shown here either.
 *
 * No zero-record branch: unlike ABD0013, which opens with two blank boxes and a "nothing matched"
 * caption (uABD0013.PAS:63-79), this dialog is only ever shown for a run that changed something. A run
 * that matched nothing reports through an error toast instead — a deliberate deviation.
 *
 * Tolerates a null result because the dialog's content is rendered unconditionally — see the Popup.
 */
const resultRows = (result) => {
    const value = result ?? {};

    return [
        { label: RESULT_LABELS.BatchNumber, value: value.BatchNumber ?? "" },
        { label: RESULT_LABELS.RecordsProcessed, value: value.RecordsProcessed ?? "" },
    ];
};

/**
 * WM "Update Free Days and Grace Periods" (legacy WMD0012).
 *
 * Start runs SPWM_BATCHFREEDAYSGRACEPERIOD server-side and reports the batch number and the number of
 * charges released. Preview runs the same filter and effective date through a read-only SELECT.
 *
 * The legacy Help and Exit buttons are dropped: Help opened a Delphi context topic and Exit closed the
 * MDI child, neither of which has a web equivalent.
 */
const UpdateFreeDaysGracePeriods = (props) => {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

    const formRefs = useRef({});
    const reportRef = useRef(null);
    const previewGrid = useRef(null);

    const [formFields, setFormFields] = useState(initialFormFields);
    const [filterWhereInfoList, setFilterWhereInfoList] = useState([]);
    const [busy, setBusy] = useState(BUSY.IDLE);

    /**
     * One object rather than three useStates. React 17 does not batch setState across an await, so
     * three separate updates after the fetch meant three extra renders of the page — and each one
     * rebuilt the grid's toolbar template, remounting GridLayoutManager and firing another
     * GetGridLayouts request.
     */
    const [preview, setPreview] = useState({ rows: [], visible: false, hasRun: false });
    const [previewLayout, setPreviewLayout] = useState(DEFAULT_LAYOUT_NAME);

    const [result, setResult] = useState(null);

    const isBusy = busy !== BUSY.IDLE;

    const postOptions = useCallback(
        () => ({
            headers: {
                "Content-Type": "application/json",
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
            timeout: REQUEST_TIMEOUT_MS,
        }),
        [accessToken]
    );

    /**
     * CommonReport fires this once, from loadReport's .then. It stays correct because the editable
     * filter grid is bound to an ArrayStore wrapping this same array instance, which cell edits mutate
     * in place — so the reference captured here stays live. Do not clone it per render.
     */
    const handleReportStateChange = useCallback((reportState) => {
        setFilterWhereInfoList(reportState?.filterWhereInfoList ?? []);
    }, []);

    // Date to Use = Specific As Of Date makes the date picker live and the day offset dead; Calculated
    // from Current Date does the reverse (uWMD0012.pas:295-308).
    const applyDateRules = useCallback((dateToUse) => {
        const specific = dateToUse === DateToUse.SpecificDate;
        DisableEnableControls(FIELD.AsOfDate, !specific, formRefs);
        DisableEnableControls(FIELD.CalculatedDays, specific, formRefs);
    }, []);

    const handleFieldChange = useCallback(
        (name, value) => {
            // Only DateToUse and CalculatedDays drive the rule. AsOfDate is deliberately absent: this
            // handler WRITES AsOfDate, and reacting to that write is an infinite loop — Delphi guards
            // the same recursion with fInSetDate (uWMD0012.pas:284/321/327).
            if (name === FIELD.DateToUse) {
                applyDateRules(value);
                setFormFields((previous) =>
                    value === DateToUse.SpecificDate
                        ? // The legacy zeroes the offset when switching to a specific date
                          // (uWMD0012.pas:290-291).
                          { ...previous, CalculatedDays: 0 }
                        : { ...previous, AsOfDate: addDays(new Date(), Number(previous.CalculatedDays) || 0) });
            } else if (name === FIELD.CalculatedDays) {
                setFormFields((previous) =>
                    previous.DateToUse === DateToUse.SpecificDate
                        ? previous
                        : { ...previous, AsOfDate: addDays(new Date(), Number(value) || 0) });
            }
        },
        [applyDateRules]
    );

    const handleFormLoad = useCallback(() => {
        // FormField calls onFormLoad in its fetchData finally, before React has committed the child
        // Fields, so formRefs are not populated yet. Same shape as calculateDeferredRevenue.
        setTimeout(() => {
            loadLookupFromStaticDataSource(FIELD.DateToUse, DATE_TO_USE_OPTIONS, formFields, formRefs);
            loadLookupFromStaticDataSource(FIELD.ZeroCharges, ZERO_CHARGES_OPTIONS, formFields, formRefs);
            applyDateRules(formFields.DateToUse);
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Belt-and-braces re-apply, mirroring the sibling screen.
    useEffect(() => {
        applyDateRules(formFields.DateToUse);
    }, [formFields.DateToUse, applyDateRules]);

    /**
     * The one rule the layout schema cannot express: Fields.js emits a static RequiredRule, but As Of
     * Date is only required in Specific mode. Re-checked server-side, since a direct POST skips this.
     */
    const validate = useCallback(() => {
        if (formFields.DateToUse === DateToUse.SpecificDate && !formFields.AsOfDate) {
            notify({ message: MESSAGES.FREE_DAYS_AS_OF_DATE_REQUIRED, width: 450 }, "error", 4000);
            const ref = formRefs.current?.[FIELD.AsOfDate];
            if (ref && typeof ref.instance === "function") {
                ref.instance().focus();
            }
            return false;
        }
        return true;
    }, [formFields]);

    // The client never computes the effective date — it sends the raw trio and the server derives it,
    // so Preview and Start can never resolve to different days. The mirrored AsOfDate is display only.
    const buildRequest = useCallback(
        (rows, extra) => ({
            DateToUse: formFields.DateToUse,
            AsOfDate: formFields.AsOfDate,
            CalculatedDays: Number(formFields.CalculatedDays) || 0,
            ZeroCharges: formFields.ZeroCharges,
            Comment: (formFields.Comment || "").slice(0, COMMENT_MAX_LENGTH),
            FilterWhereInfoList: rows || [],
            ...extra,
        }),
        [formFields]
    );

    // Commits any in-progress filter cell before reading the rows — see common/reportFilters.js for why
    // the onReportStateChange array is the wrong source at submit time.
    const readFilterRows = useCommittedFilterRows(reportRef, filterWhereInfoList);

    // Stable identity: an inline arrow here would change on every render and, via the Popup, keep the
    // grid subtree re-rendering.
    const hidePreview = useCallback(() => {
        setPreview((previous) => ({ ...previous, visible: false }));
    }, []);

    const closeResult = useCallback(() => setResult(null), []);

    const handlePreview = useCallback(async () => {
        if (!validate()) {
            return;
        }

        // Commits any in-progress filter cell first, so Preview matches what the user typed.
        const filterRows = await readFilterRows();

        setBusy(BUSY.PREVIEWING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMUpdateFreeDays.GetCharges}`,
                JSON.stringify(buildRequest(filterRows)),
                postOptions()
            );

            const payload = response?.data;

            // DEV-5355 F2. A row array is the ONLY shape treated as success; anything else is an error,
            // whatever the envelope claims.
            //
            // This originally read Resultdata straight into the grid, so a server-side failure arrived as
            // Resultdata: null, became [], and opened the popup reporting "no charges matched" with the
            // real message discarded. Checking Success alone then fixed the cases that fail BEFORE the
            // query runs but left the same hole for one that fails DURING it — an out-of-range date
            // literal, say, which SQL rejects mid-query. Requiring the array closes both, and any future
            // failure mode, without depending on the envelope being filled in correctly.
            if (payload?.Success === false || !Array.isArray(payload?.Resultdata)) {
                setPreview({ rows: [], visible: false, hasRun: false });
                notify({ message: payload?.Message || MESSAGES.ERROR, width: 450 }, "error", 5000);
                return;
            }

            // An empty array IS legitimate — the grid says so via noDataText.
            setPreview({ rows: payload.Resultdata, visible: true, hasRun: true });
        } catch (error) {
            setPreview({ rows: [], visible: false, hasRun: false });
            notify({ message: getApiErrorMessage(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [validate, readFilterRows, buildRequest, postOptions]);

    /**
     * The single Start poster, used for both passes of the confirmation round-trip. Returns the parsed
     * payload, or null when the call failed (the toast has already been shown).
     *
     * `updateMissingReleaseDates` is undefined on the first pass — JSON.stringify drops undefined
     * properties, so the server sees no answer and can ask.
     */
    const postStart = useCallback(
        async (rows, updateMissingReleaseDates) => {
            setBusy(BUSY.STARTING);
            try {
                const response = await axios.post(
                    `${window._env_.REACT_APP_APIS}/${routes.WMUpdateFreeDays.Start}`,
                    JSON.stringify(buildRequest(rows, { UpdateMissingReleaseDates: updateMissingReleaseDates })),
                    postOptions()
                );

                const payload = response?.data;
                return { payload, data: payload?.Resultdata ?? {} };
            } catch (error) {
                notify({ message: getApiErrorMessage(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
                return null;
            } finally {
                setBusy(BUSY.IDLE);
            }
        },
        [buildRequest, postOptions]
    );

    const showStartResult = useCallback((payload, data) => {
        // The dialog reports a run that changed something; everything else is an error toast. That
        // covers a server-side refusal (validation, over-long filter — no Resultdata at all) and a run
        // that matched no charges, where the server sends Messages.NoRecordsFoundWithinRange.
        //
        // Note this is NOT what Delphi does: ABD0013 opens either way, showing blank boxes and the
        // "nothing matched" text in its caption band (uABD0013.PAS:63-79). A toast was preferred here.
        if (payload?.Resultdata == null || (data.RecordsProcessed ?? 0) <= 0) {
            notify(
                { message: payload?.Message || MESSAGES.NO_RECORD_FOUND_RANGE, width: 450 },
                "error",
                5000
            );
            return;
        }

        setResult(data);
    }, []);

    const handleStart = useCallback(async () => {
        if (!validate()) {
            return;
        }

        // Read once, before either pass, and reuse for both: committing the grid twice would be harmless
        // but the two passes must post the SAME rows, or the confirmed run could act on a different set
        // than the one that raised the question.
        const filterRows = await readFilterRows();

        // First pass sends no decision. If charges with an empty Release Date exist the server stops
        // without touching anything and asks — the HTTP stand-in for the desktop form's mid-process
        // modal (uWMD0012.pas:159-171).
        const first = await postStart(filterRows, undefined);
        if (!first) {
            return;
        }

        if (first.data.RequiresMissingReleaseDateDecision) {
            // Dismissing without choosing resolves undefined, which means "no" — matching the legacy
            // mbNo path, where the batch still runs. The second pass always sends a definite boolean, so
            // the server cannot ask twice and this cannot loop.
            // Plain confirm(): Yes then No, with Yes focused.
            //
            // Delphi defaults to No — ab_messagedlgEx(..., [mbYes, mbNo], 0, 1) passes DefaultBtn = 1 and
            // uInterface.pas:3852-3866 marks that button Default := True — so Enter DECLINES the repair in
            // AI8. That is not reproduced here. DevExtreme's dialog hard-codes focusing the first toolbar
            // button in its own onShown, and the only override is `popupOptions`, which is honoured at
            // runtime but is absent from the public CustomDialogOptions typing — not something to depend
            // on for a focus ring. Buttons stay in Delphi's order and neither is styled as primary, so
            // nothing implies a recommendation the dialog cannot honour.
            const answer = await confirm(first.payload.Message, PAGE_TITLE);
            const second = await postStart(filterRows, answer === true);
            if (second) {
                showStartResult(second.payload, second.data);
            }
            return;
        }

        showStartResult(first.payload, first.data);
    }, [validate, readFilterRows, postStart, showStartResult]);

    const handleReset = useCallback(() => {
        setFormFields(initialFormFields());
        setPreview({ rows: [], visible: false, hasRun: false });
        setResult(null);
        // Re-fetches the field definitions, rebuilds the filter store and re-fires onReportStateChange
        // with a fresh array (wbUpdateFreeDaysGracePeriods.Reset, uWMD0012.pas:457).
        if (reportRef.current && typeof reportRef.current.loadReport === "function") {
            reportRef.current.loadReport();
        }
    }, []);

    return (
        <>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={isBusy}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
                message={
                    busy === BUSY.STARTING
                        ? MESSAGES.UPDATING_FREE_DAYS
                        : MESSAGES.PREVIEWING_FREE_DAYS_CHARGES
                }
            />

            <div className="row">
                <div className="col-md-12 main-page">
                    <Toolbar className="header">
                        <Item location="before">
                            <div className="title">
                                <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>{PAGE_TITLE}</h6>
                            </div>
                        </Item>

                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Start} type="default" onClick={handleStart} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Preview} onClick={handlePreview} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Reset} onClick={handleReset} disabled={isBusy} />
                        </Item>
                    </Toolbar>
                </div>
            </div>

            <div className="ufd-page">
                <div className="op-filter">
                    <CommonReport
                        name={REPORT_NAME}
                        fullName={REPORT_FULL_NAME}
                        onReportStateChange={handleReportStateChange}
                        innerRef={reportRef}
                    />
                </div>

                <div className="op-parameters">
                    <FormField
                        currrentUser={currentUser}
                        layoutId={LAYOUT_ID}
                        layoutName={LAYOUT_NAME}
                        defaultFormSchema={ParametersLayout}
                        formFields={formFields}
                        setformFields={setFormFields}
                        handleFieldValueChange={handleFieldChange}
                        DYNAMICFORMSETTING={dynamicFormSetting}
                        formRefs={formRefs}
                        onFormLoad={handleFormLoad}
                    />
                </div>
            </div>

            <Popup
                visible={preview.visible}
                onHiding={hidePreview}
                hideOnOutsideClick={true}
                showCloseButton={true}
                title={PREVIEW_TITLE}
                width="94vw"
                height="88vh"
                wrapperAttr={PREVIEW_POPUP_ATTR}
            >
                {/*
                    The grid FILLS the popup — see .op-preview-popup in ../common/operationScreen.css.
                    It used to be height="calc(88vh - 110px)", which had to guess the popup's own chrome;
                    guessing high leaves a dead strip at the bottom and guessing low clips the grid.

                    No ScrollView wrapper: the grid owns both scroll axes, and an outer scroller would
                    give it an unbounded width to lay out against, which kills the horizontal scrollbar
                    the 40-odd columns need.
                */}
                <div className="op-preview-body">
                    <div className="op-preview-grid">
                        <OperationResultGrid
                            rows={preview.rows}
                            columns={PREVIEW_COLUMNS}
                            keyExpr={PREVIEW_KEY_EXPR}
                            noDataText={preview.hasRun ? MESSAGES.FREE_DAYS_NO_CHARGES_FOUND : ""}
                            height="100%"
                            gridRef={previewGrid}
                            gridKey={PREVIEW_GRID_KEY}
                            currentLayout={previewLayout}
                            setCurrentLayout={setPreviewLayout}
                            accessToken={accessToken}
                        />
                    </div>
                </div>
            </Popup>

            {/*
                The legacy result dialog, TfrmABD0013 (uWMD0012.pas:195).

                OK is the only way out, matching the Delphi form: uABD0013.DFM sets BorderStyle bsSingle
                with BorderIcons reduced (no close box) and FormStyle fsStayOnTop, and uWMD0012.pas:200
                shows it with ShowModal — so there is no X and clicking away does nothing. A batch result
                carrying a batch number is worth an explicit acknowledgement.
            */}
            <Popup
                visible={result != null}
                onHiding={closeResult}
                hideOnOutsideClick={false}
                showCloseButton={false}
                title={MESSAGES.PROCESS_COMPLETE}
                width={420}
                height="auto"
            >
                {/*
                    Rendered unconditionally. Wrapping this in `{result && …}` meant that when DevExtreme
                    created the Popup's content template `result` was still null, so it captured an empty
                    template and never filled it — the modal opened showing its title and nothing else.
                    Same trap as a conditionally-rendered config component. resultRows tolerates null so
                    the hidden state renders harmless placeholders.
                */}
                <div className="op-result">
                    {/* Delphi shows the caller-supplied caption in a read-only box above the counts. */}
                    <div className="op-result-caption">{RESULT_CAPTION}</div>
                    {resultRows(result).map((row) => (
                        <div className="op-result-row" key={row.label}>
                            <span className="op-result-label">{row.label}</span>
                            <span className="op-result-value">{row.value}</span>
                        </div>
                    ))}
                    <div className="op-result-actions">
                        <Button text={RESULT_OK} type="default" onClick={closeResult} />
                    </div>
                </div>
            </Popup>
        </>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(UpdateFreeDaysGracePeriods);
